import { Link } from 'react-router-dom';
import { MoreHorizontal, ArrowRightLeft, User } from 'lucide-react';
import UrgencyDot from '@/components/shared/UrgencyDot.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import { usePeekStore } from '@/stores/peek-store.js';
import { openContactPeek } from '@/lib/links.js';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu.jsx';
import { formatDate } from '@/lib/format.js';
import useDealActions from '@/hooks/useDealActions.js';
import DealActionsSheets from '@/components/sheets/DealActionsSheets.jsx';

// Compact kanban card with per-card ⋯ quick-action menu
// (Vision §V2 fix #1, 2026-05-20). Reuses the same useDealActions hook
// + DealActionsSheets module that DealActionBar uses, so the set of
// available actions and the sheets that open are byte-identical across
// surfaces.
//
// Card spine (redesign locked 2026-06-15, design-reference/PipelineDealCard.jsx
// + pipeline.preview.html) = WHO / WHAT / WHEN / VALUE:
//   line 1  who   — named insured (bold) + urgency dot; when (date / PAST DUE) top-right
//   line 2  what  — aircraft: mono N-number + make/model (+N for a fleet)
//   line 3  who²  — primary contact, own row + person glyph; OMITTED when the
//                   contact just repeats the insured (individual-named deals)
//   foot    value — premium (mono); `exp` suffix pre-quote (expiring premium),
//                   the quoted figure once a quote is in. One quiet status chip.
// Dropped from the prior card: ND-APP number (searchable via toolbar, not a
// glanceable signal) and the 3-badge carrier/quote/readiness soup.
//
// The ⋯ quick-action menu + DealActionsSheets wiring is unchanged from shipping.
// Right-click on the card body still triggers onContextMenu (legacy power-user
// shortcut); the Link onContextMenu is preserved so it doesn't open the OS menu.

const money = (n) => (n == null || !Number.isFinite(Number(n)) ? null : '$' + Number(n).toLocaleString('en-US'));

function whenLabel(item) {
  const d = item.days_until_expiration;
  if (d == null) return null;
  if (d < 0) return { text: `PAST DUE: ${d}d`, past: true };
  if (item.policy_expiration) return { text: formatDate(item.policy_expiration, { month: 'short', day: 'numeric' }), past: false };
  return { text: `${d}d`, past: false };
}

// One quiet status chip: readiness pre-quote, else presenting / quote count.
function StatusChip({ item }) {
  if (item.renewal_readiness && !item.renewal_readiness.ready) {
    return <Badge variant="warning" className="text-[10px]">Missing {item.renewal_readiness.missing?.length || 0}</Badge>;
  }
  if (item.renewal_readiness?.ready) return <Badge variant="success" className="text-[10px]">Ready</Badge>;
  const presenting = item.quotes?.visible || 0;
  if (presenting > 0) return <Badge variant="success" className="text-[10px]">{presenting} presenting</Badge>;
  const q = item.quotes?.total || 0;
  if (q > 0) return <Badge variant="info" className="text-[10px]">{q} quote{q === 1 ? '' : 's'}</Badge>;
  return null;
}

export default function PipelineDealCard({ item, onContextMenu, onChange }) {
  const target = `/account/${encodeURIComponent(item.account_id || item.deal_id)}/deal/${encodeURIComponent(item.deal_id)}`;
  const when = whenLabel(item);

  // value: explicit field if the backend supplies it, else the deal premium.
  // value_basis: 'quoted' once any quote is in, else 'expiring' (the `exp` tag).
  const val = money(item.value ?? item.premium);
  const valueBasis = item.value_basis || ((item.quotes?.total || 0) > 0 ? 'quoted' : 'expiring');

  // aircraft is an array on the item ({tail_number, make_model}); show the
  // first, with a +N tag for a fleet.
  const fleet = Array.isArray(item.aircraft) ? item.aircraft : [];
  const ac0 = fleet[0] || null;
  const tail = ac0?.tail_number || '';
  const model = ac0?.make_model || '';
  const more = fleet.length > 1 ? ` +${fleet.length - 1}` : '';

  // Omit the contact row when the contact just repeats the insured.
  const contact = item.primary_contact;
  const showContact = contact?.name && contact.name !== item.named_insured;

  // Pass the loaded item directly so useDealActions doesn't fire one
  // /api/tracon/deals/:id fetch per card on kanban render.
  const openPeek = usePeekStore((s) => s.openPeek);
  const actionState = useDealActions(item);
  const { actions, openers } = actionState;

  function handleContextMenu(e) {
    if (!onContextMenu) return;
    e.preventDefault();
    onContextMenu(item, { x: e.clientX, y: e.clientY });
  }

  function handleMoveTo(e) {
    if (!onContextMenu) return;
    const rect = e?.currentTarget?.getBoundingClientRect?.();
    const position = rect
      ? { x: rect.right, y: rect.bottom }
      : { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    // Defer past DropdownMenu unmount so the new MoveToMenu Popover
    // doesn't catch the closing dropdown's dismiss events.
    setTimeout(() => onContextMenu(item, position), 200);
  }

  return (
    <div className="group relative">
      <Link
        to={target}
        onContextMenu={handleContextMenu}
        className="block rounded-card border border-border bg-surface p-3 pr-9 transition-colors hover:border-border-strong active:bg-border/20"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-center gap-1.5">
            <UrgencyDot urgency={item.urgency} />
            <span className="truncate text-sm font-semibold text-text">{item.named_insured || '—'}</span>
          </div>
          {when ? (
            <span className={`shrink-0 text-[11px] font-medium ${when.past ? 'text-accent' : 'text-text-muted'}`}>
              {when.text}
            </span>
          ) : null}
        </div>

        {tail || model ? (
          <div className="mt-1 truncate text-[11px] text-text-dim">
            {tail ? <span className="font-mono text-text-muted">{tail}</span> : null}
            {model ? <>{tail ? ' · ' : ''}{model}</> : null}
            {more}
          </div>
        ) : null}

        {showContact ? (
          <div className="mt-1 flex items-center gap-1.5 text-[11px] text-text-dim">
            <User size={12} strokeWidth={2} className="shrink-0" />
            {contact.crm_id ? (
              <button
                type="button"
                data-interactive
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); openPeek(openContactPeek(contact.crm_id)); }}
                className="truncate hover:underline"
              >
                {contact.name}
              </button>
            ) : (
              <span className="truncate">{contact.name}</span>
            )}
          </div>
        ) : null}

        {item.cancellation_progress?.sentence ? (
          <div className="mt-1.5 text-[11px] font-medium text-text" data-cancellation-progress="in_progress">
            {item.cancellation_progress.sentence}
          </div>
        ) : null}

        <div className="mt-2.5 flex items-center justify-between gap-2">
          <span className="font-mono text-[15px] font-medium text-text">
            {val || '—'}
            {val && valueBasis === 'expiring' ? <span className="ml-0.5 text-[10px] font-normal text-text-dim">exp</span> : null}
          </span>
          <StatusChip item={item} />
        </div>
      </Link>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Quick actions"
            // Click anywhere inside the trigger is for the menu, not
            // for the Link below — stopPropagation on the surrounding
            // div prevents nav.
            onClick={(e) => e.stopPropagation()}
            className="absolute right-1.5 top-1.5 flex h-7 w-7 items-center justify-center rounded-full text-text-dim hover:bg-border/40 hover:text-text md:opacity-0 md:group-hover:opacity-100"
          >
            <MoreHorizontal size={16} strokeWidth={1.5} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          sideOffset={6}
          collisionPadding={16}
          className="w-52"
        >
          <DropdownMenuLabel>Quick actions</DropdownMenuLabel>
          {actions.map(({ key, label, Icon }) => (
            <DropdownMenuItem key={key} onSelect={openers[key]}>
              <Icon size={14} strokeWidth={1.5} className="text-text-dim" />
              <span>{label}</span>
            </DropdownMenuItem>
          ))}
          {onContextMenu ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={handleMoveTo}>
                <ArrowRightLeft size={14} strokeWidth={1.5} className="text-text-dim" />
                <span>Move to…</span>
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>

      <DealActionsSheets
        dealId={item.deal_id}
        actionState={actionState}
        onChange={onChange}
      />
    </div>
  );
}
